import AddIcon from '@mui/icons-material/Add';
import {
  Autocomplete,
  Box,
  Button,
  Stack,
  styled,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { type HTMLAttributes, memo, type ReactNode, useCallback, useMemo } from 'react';
import type { WorkflowRaw, WorkflowVersionRaw } from 'src/types';

dayjs.extend(relativeTime);

const ActiveStatusBadge = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  paddingTop: theme.spacing(0.25),
  paddingBottom: theme.spacing(0.25),
  borderRadius: 999,
  fontSize: '0.75rem',
  fontWeight: 600,
  color: theme.palette.common.white,
  backgroundColor: theme.palette.success.main,
}));

type WorkflowItem = Pick<WorkflowRaw, 'workflowId' | 'name'>;

type WorkflowVersionItem = Pick<
  WorkflowVersionRaw,
  'workflowVersionId' | 'version' | 'isActive' | 'updatedAt'
>;

type AutocompleteOptionProps = HTMLAttributes<HTMLLIElement> & { key: string };

interface WorkflowSelectionToolbarProps {
  workflows: WorkflowItem[];
  workflowVersions: WorkflowVersionItem[];
  selectedWorkflowId?: string;
  selectedVersionId?: string;
  workflowSelectDisabled: boolean;
  versionSelectDisabled: boolean;
  createDraftDisabled: boolean;
  onWorkflowChange: (workflowId?: string) => void;
  onVersionChange: (workflowVersionId?: string) => void;
  onCreateDraft: () => void;
  versionActions?: ReactNode;
}

const dropdownWidthStyles = {
  width: { xs: '100%', sm: 220, md: 260 },
  maxWidth: 320,
};

const WorkflowSelectionToolbarComponent = ({
  workflows,
  workflowVersions,
  selectedWorkflowId,
  selectedVersionId,
  workflowSelectDisabled,
  versionSelectDisabled,
  createDraftDisabled,
  onWorkflowChange,
  onVersionChange,
  onCreateDraft,
  versionActions,
}: WorkflowSelectionToolbarProps) => {
  const theme = useTheme();
  const getRelativeTime = useCallback(
    (value?: string | null) => (value ? dayjs(value).fromNow() : '—'),
    [],
  );

  const dropdownPaperStyles = useMemo(
    () => ({
      elevation: 6,
      sx: {
        mt: 1,
        border: `1px solid ${theme.palette.divider}`,
        backgroundImage: 'none',
        backgroundColor:
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.paper, 0.96)
            : theme.palette.common.white,
        boxShadow:
          theme.palette.mode === 'dark'
            ? '0 12px 32px rgba(0,0,0,0.45)'
            : '0 12px 28px rgba(55, 65, 81, 0.2)',
      },
    }),
    [
      theme.palette.background.paper,
      theme.palette.common.white,
      theme.palette.divider,
      theme.palette.mode,
    ],
  );

  const workflowOptions = useMemo<WorkflowItem[]>(
    () =>
      workflows.map((workflow) => ({
        ...workflow,
        workflowId: String(workflow.workflowId),
      })),
    [workflows],
  );

  const versionOptions = useMemo<WorkflowVersionItem[]>(
    () =>
      workflowVersions.map((workflowVersion) => ({
        ...workflowVersion,
        workflowVersionId: String(workflowVersion.workflowVersionId),
      })),
    [workflowVersions],
  );

  const workflowValue = useMemo<WorkflowItem | null>(
    () =>
      workflowOptions.find((workflow) => workflow.workflowId === selectedWorkflowId) ?? null,
    [selectedWorkflowId, workflowOptions],
  );

  const versionValue = useMemo<WorkflowVersionItem | null>(
    () =>
      versionOptions.find((workflowVersion) => {
        return workflowVersion.workflowVersionId === selectedVersionId;
      }) ?? null,
    [selectedVersionId, versionOptions],
  );

  const renderVersionOption = (option: WorkflowVersionItem) => (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      width="100%"
      spacing={2}
    >
      <Stack spacing={0.25}>
        <Typography variant="subtitle2" fontWeight={600}>
          Version {option.version}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Updated {getRelativeTime(option.updatedAt)}
        </Typography>
      </Stack>
      {!!option.isActive && <ActiveStatusBadge>Active</ActiveStatusBadge>}
    </Stack>
  );

  return (
    <Stack
      spacing={2}
      sx={{
        width: '100%',
      }}
    >
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        sx={{
          width: '100%',
          alignItems: { xs: 'stretch', lg: 'center' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1.5, sm: 2 }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          flexWrap="wrap"
          sx={{ flex: 1, minWidth: 0 }}
        >
          <Autocomplete<WorkflowItem, false, boolean>
            value={workflowValue}
            onChange={(_, value) => onWorkflowChange(value?.workflowId)}
            disabled={workflowSelectDisabled}
            options={workflowOptions}
            getOptionLabel={(option) => option.name ?? ''}
            isOptionEqualToValue={(option, value) => option.workflowId === value?.workflowId}
            noOptionsText={workflowSelectDisabled ? 'No workflows' : 'No matches'}
            sx={dropdownWidthStyles}
            ListboxProps={{
              sx: {
                py: 0.5,
              },
            }}
            slotProps={{
              paper: dropdownPaperStyles,
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Workflow"
                placeholder="Select Workflow"
                size="small"
              />
            )}
            renderOption={(props: AutocompleteOptionProps, option) => {
              const { key, ...optionProps } = props;
              return (
                <li key={key} {...optionProps}>
                  <Typography variant="body2" fontWeight={600}>
                    {option.name}
                  </Typography>
                </li>
              );
            }}
          />

          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Autocomplete<WorkflowVersionItem, false, boolean>
              value={versionValue}
              onChange={(_, value) => onVersionChange(value?.workflowVersionId)}
              disabled={versionSelectDisabled}
              options={versionOptions}
              getOptionLabel={(option) => `Version ${option.version}`}
              isOptionEqualToValue={(option, value) =>
                option.workflowVersionId === value?.workflowVersionId
              }
              noOptionsText={versionSelectDisabled ? 'Select workflow first' : 'No matches'}
              sx={dropdownWidthStyles}
              ListboxProps={{
                sx: {
                  py: 0.5,
                  '& .MuiAutocomplete-option': {
                    alignItems: 'flex-start',
                    gap: 1,
                  },
                },
              }}
              slotProps={{
                paper: dropdownPaperStyles,
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Version"
                  placeholder="Select Version"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: versionValue?.isActive ? (
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box
                          component="span"
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: theme.palette.success.main,
                            boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
                          }}
                        />
                        {params.InputProps.endAdornment}
                      </Stack>
                    ) : (
                      params.InputProps.endAdornment
                    ),
                  }}
                />
              )}
              renderOption={(props: AutocompleteOptionProps, option) => {
                const { key, ...optionProps } = props;
                return (
                  <li key={key} {...optionProps}>
                    {renderVersionOption(option)}
                  </li>
                );
              }}
            />

            {versionActions}
          </Stack>
        </Stack>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onCreateDraft}
          disabled={createDraftDisabled}
          sx={{ alignSelf: { xs: 'stretch', lg: 'center' } }}
        >
          Create Draft Version
        </Button>
      </Stack>
    </Stack>
  );
};

WorkflowSelectionToolbarComponent.displayName = 'WorkflowSelectionToolbar';

export const WorkflowSelectionToolbar = memo(WorkflowSelectionToolbarComponent);
